import { useRef, useState } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import Select, { MultiValue, PropsValue, SingleValue, OptionsOrGroups, GroupBase } from "react-select";

import { HubInputProps } from "./types";


const HubInputField =
<T extends FieldValues, K extends FieldPath<T>>({
  isRowBased,
  isRowInsideAnother,
  inputLabel,
  inputId,
  isRequired,
  inputPlaceholder,
  inputType,
  field,
  error,
  onKeyDown,
  onBlur,
  isDropdown,
  isSearchable,
  dropdownData,
  isMulti,
  getSearchPhraseFromDropdown,
  isTypeAhead,
  classNames,
  parentClassNames,
  isDisabled
}: HubInputProps<T, K>): JSX.Element => {

  const searchRef = useRef("");    
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isKeySelectionRef, setIsKeySelctionRef] = useState<Boolean>(false);

  const getValue = (value: string | string[]) => {
    if (dropdownData) {
      if (value === "") return null;

      const data = dropdownData.filter((option) => {
        if (Array.isArray(value)) {
          return value.indexOf((option as unknown as { value: string; label: string; }).value) > -1;
        } else {
          return (option as unknown as { value: string; label: string; }).value === value;
        }
      });

      if (data) {
        if (isMulti)
          return data as unknown as MultiValue<string>;
        else
          return data[0] as unknown as PropsValue<string>;
      }
    }
  };

  // useEffect(() => console.log({ updateValue: field.value }), [field.value])

  const onChangeSelect = (value: SingleValue<string | { label: string; value: string; }> | MultiValue<string | { label: string; value: string; }>) => {
    if (!isKeySelectionRef) {
      if (Array.isArray(value))
        field.onChange(
          value.map((val) => (val as unknown as { value: string; label: string; }).value)
        );
      else
        field.onChange((value as unknown as { value: string; label: string; }).value);
    } else {
      setIsKeySelctionRef(false)
    }
  };

  const onBlurOnInput = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (onBlur) onBlur(event);
    field.onBlur();
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isTypeAhead || !dropdownData || dropdownData.length === 0) return;
    // Ignore special keys
    if (e.key.length !== 1 && e.key !== "Backspace") return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (e.key === "Backspace")
      searchRef.current = searchRef.current.slice(0, -1);
    else
      searchRef.current += e.key.toLowerCase();

    const flatOptions: { label: string; value: string }[] = (
      dropdownData as unknown as OptionsOrGroups<
        { label: string; value: string },
        GroupBase<{ label: string; value: string }>
      >
    ).flatMap(opt =>
      "options" in opt ? opt.options : [opt]
    );

    const match = flatOptions.find(opt =>
      opt.label.toLowerCase().startsWith(searchRef.current)
    );

    if (match) {
      setIsKeySelctionRef(true)
      field.onChange(match.value);
    }

    timeoutRef.current = setTimeout(() => {
      searchRef.current = "";
    }, 1000);
  };

  const handleInputChange = (newValue: string) => {
    searchRef.current = newValue;
    if (getSearchPhraseFromDropdown) getSearchPhraseFromDropdown(newValue);
    return newValue;
  };



  const RowBasedInput = (
    <div className="row mb-3">
      {inputLabel ? (
        <label htmlFor={inputId} className="form-label col-lg-2 col-md-4 col-12 p-2">{inputLabel} {isRequired ? '*' : null}</label>
      ) : null}
      <div className={inputLabel ? "col-lg-10 col-md-8 col-12" : "col-12"}>
        {isDropdown ? (
          <Select
            options={dropdownData}
            id={inputId}
            placeholder={inputPlaceholder}
            isSearchable={isSearchable}
            isMulti={isMulti}
            classNames={{
              control(_props) {
                return `input-group ${error && error.message ? "border-danger" : ""} ${classNames || ""}`
              },
            }}
            {...field}
            noOptionsMessage={() => "Select Option..."}
            value={getValue(field.value)}
            onChange={(value) => onChangeSelect(value)}
            onInputChange={handleInputChange}
            onKeyDown={handleKeyDown}
            isDisabled={isDisabled}
          />
        ) : (
          <input disabled={isDisabled} type={inputType ? inputType : "text"} className={`form-control ${error && error.message ? 'is-invalid' : ''}  ${classNames || ""}`} id={inputId} placeholder={inputPlaceholder} onKeyDown={onKeyDown} {...field} onBlur={onBlurOnInput} />
        )}
        {error && error.message ? (
          <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2 "></div>
        ) : null}
      </div>
    </div>
  );

  const RowInsideRowBasedInput = (
    <div className="row mb-3">
      {inputLabel ? (
        <label htmlFor={inputId} className="form-label col-lg-4 col-md-4 col-12">
          {inputLabel} {isRequired ? '*' : null}
        </label>
      ) : null}
      <div className={inputLabel ? 'col-lg-8 col-md-8 col-12' : 'col-12'}>
        {isDropdown ? (
          <Select
            options={dropdownData}
            id={inputId}
            classNames={{
              control(_props) {
                return `input-group ${error && error.message ? "border-danger" : ""} ${classNames || ""}`
              },
            }}
            placeholder={inputPlaceholder}
            isSearchable={isSearchable}
            isMulti={isMulti}
            {...field}
            noOptionsMessage={() => "Select Option..."}
            value={getValue(field.value)}
            onChange={(value) => onChangeSelect(value)}
            onInputChange={handleInputChange}
            onKeyDown={handleKeyDown}
            isDisabled={isDisabled}
          />
        ) : (
          <input disabled={isDisabled} type={inputType ? inputType : "text"} className={`form-control ${error && error.message ? 'is-invalid' : ''}  ${classNames || ""}`} id={inputId} placeholder={inputPlaceholder} onKeyDown={onKeyDown} {...field} onBlur={onBlurOnInput} />
        )}
        {error && error.message ? (
          <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2 "></div>
        ) : null}
      </div>
    </div>
  );

  const SimpleInput = (
    <div className={parentClassNames ? parentClassNames : "mb-3"}>
      {inputLabel ? (
        <label htmlFor={inputId} className="form-label justify-content-start">{inputLabel} {isRequired ? '*' : null}</label>
      ) : null}
      {isDropdown ? (
        <Select
          options={dropdownData}
          id={inputId}
          placeholder={inputPlaceholder}
          isSearchable={isSearchable}
          isMulti={isMulti}
          classNames={{
            control(_props) {
              return `input-group ${error && error.message ? "border-danger" : ""} ${classNames || ""}`
            },
          }}
          {...field}
          noOptionsMessage={() => "Select Option..."} 
          value={getValue(field.value)}
          onChange={(value, action) => {console.log({ "actionObj": action.option }); onChangeSelect(value)}}
          onInputChange={getSearchPhraseFromDropdown}
          onKeyDown={handleKeyDown}
          isDisabled={isDisabled}
        />
      ) : (
        <input disabled={isDisabled} type={inputType ? inputType : "text"} className={`form-control ${error && error.message ? 'is-invalid' : ''}  ${classNames || ""}`} placeholder={inputPlaceholder} onKeyDown={onKeyDown} id={inputId} {...field} onBlur={onBlurOnInput} />
      )}
      {error && error.message ? (
        <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
      ) : null}
    </div>
  );

  return isRowBased ? (isRowInsideAnother ? RowInsideRowBasedInput : RowBasedInput) : SimpleInput;
};

export default HubInputField;

